<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import List from '@shared/components/List/List.vue';
    import { formatDate } from '@shared/utils/formatDateTime';
    import RoleEditor from './RoleEditor.vue';
    import RoleDeleteModal from './RoleDeleteModal.vue';
    import {
        getRoles,
        getRole,
        getFeatures,
        getFeatureRequirements,
        type FeatureRequirement,
        type FeatureSummary,
        type Role,
        type RoleSummary,
    } from './Roles.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';

    const emit = defineEmits<{
        rolesChanged: [];
    }>();

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const hasWriteScope = scopeAccess.hasScope({ Feature: 'Account', Permission: Permissions.Write });

    const state = reactive<{
        roles: RoleSummary[];
        features: FeatureSummary[];
        featureRequirements: FeatureRequirement[];
        loading: boolean;
        editingRole: Role | null;
        showEditor: boolean;
        deletingRole: RoleSummary | null;
        showDeleteModal: boolean;
    }>({
        roles: [],
        features: [],
        featureRequirements: [],
        loading: true,
        editingRole: null,
        showEditor: false,
        deletingRole: null,
        showDeleteModal: false,
    });

    onMounted(async () => {
        const [roles, features, featureRequirements] = await Promise.all([getRoles(), getFeatures(), getFeatureRequirements()]);
        state.roles = roles;
        state.features = features;
        state.featureRequirements = featureRequirements;
        state.loading = false;
    });

    const tableHeaders = [
        $Resources.RolesAndPermissionsRoleLabel,
        $Resources.GenericCreatedLabel,
        $Resources.LastModifiedLabel,
        $Resources.GenericModifiedByLabel,
        ...(hasWriteScope ? [$Resources.RolesAndPermissionsDeleteLabel] : []),
    ];

    function newRole(): void {
        state.editingRole = null;
        state.showEditor = true;
    }

    async function editRole(role: RoleSummary): Promise<void> {
        state.editingRole = await getRole(role.RoleId);
        state.showEditor = true;
    }

    async function refreshRoles(): Promise<void> {
        state.roles = await getRoles();
    }

    async function onSaved(): Promise<void> {
        state.showEditor = false;
        await refreshRoles();
        emit('rolesChanged');
    }

    function preDeleteRole(role: RoleSummary): void {
        state.deletingRole = role;
        state.showDeleteModal = true;
    }

    async function onDeleted(): Promise<void> {
        state.showDeleteModal = false;
        await refreshRoles();
        emit('rolesChanged');
    }
</script>

<template>
    <h2 class="h3">{{ $Resources.RolesAndPermissionsHeading }}</h2>

    <List itemId="RoleId" :items="state.roles" displayAs="table" :tableHeaders="tableHeaders" :loading="state.loading" hideSearch class="th5-text-right">
        <template #toolbar>
            <button v-if="hasWriteScope" class="btn btn-primary" type="button" @click="newRole">{{ $Resources.CreateNewRoleButton }}</button>
        </template>
        <template #item="{ item: role }">
            <td>
                <span class="d-block strong">
                    <a v-if="hasWriteScope" href="javascript:void(0)" role="button" :title="$Resources.RoleListingEditRole" @click="editRole(role)">
                        {{ role.Name }}
                    </a>
                    <template v-else>{{ role.Name }}</template>
                </span>
                <span class="d-block">{{ role.InternalDescription }}</span>
            </td>
            <td>{{ formatDate(role.Created) }}</td>
            <td>{{ formatDate(role.Modified) }}</td>
            <td>{{ role.ModifiedBy?.Name || role.CreatedBy }}</td>
            <td class="text-right" v-if="hasWriteScope">
                <ConfirmButton buttonClasses="btn-xs" :buttonText="$Resources.RoleDeleteRoleButto" @confirm="preDeleteRole(role)" undoButton></ConfirmButton>
            </td>
        </template>
    </List>

    <RoleEditor
        :show="state.showEditor"
        :role="state.editingRole"
        :features="state.features"
        :featureRequirements="state.featureRequirements"
        @close="state.showEditor = false"
        @saved="onSaved"></RoleEditor>

    <RoleDeleteModal
        :show="state.showDeleteModal"
        :role="state.deletingRole"
        :roles="state.roles"
        @close="state.showDeleteModal = false"
        @deleted="onDeleted"></RoleDeleteModal>
</template>
